import { IDiscrepancy } from 'src/app/discrepancies/models/discrepancies.model';
import { IValidation, VerifiedTasks } from '../../models/validation.model';
import * as ValidationActions from './validation.actions';
import { Action, createReducer, on } from '@ngrx/store';

export type State = IValidation;

const initialState: State = {
  checks: null,
  verifiedTasks: null,
  loaders: {
    task: false,
    checks: false,
    verifiedTasks: false
  }
};

const validationReducer = createReducer(
  initialState,
  on(ValidationActions.ClearState, (): State => initialState),
  on(ValidationActions.GetOutputChecks, (state): State => ({ ...state, loaders: { ...state.loaders, checks: true } })),
  on(
    ValidationActions.GetOutputChecksSuccess,
    (state, { checks }): State => ({
      ...state,
      checks,
      loaders: { ...state.loaders, checks: true }
    })
  ),
  on(
    ValidationActions.GetVerifiedTasks,
    (state): State => ({ ...state, loaders: { ...state.loaders, verifiedTasks: true, checks: true } })
  ),
  on(
    ValidationActions.GetVerifiedTasksSuccess,
    (state, { tasks, tlfName }): State => ({
      ...state,
      verifiedTasks: updateVerifiedTasks(state.verifiedTasks, tlfName, tasks),
      loaders: { ...state.loaders, verifiedTasks: false, checks: false }
    })
  ),
  on(
    ValidationActions.GetVerifiedTasksFailed,
    (state): State => ({
      ...state,
      loaders: { ...state.loaders, verifiedTasks: false, checks: false }
    })
  ),

  on(ValidationActions.CreateTask, (state): State => {
    return { ...state, loaders: { ...state.loaders, task: true } };
  }),

  on(ValidationActions.CreateTaskFailed, (state): State => {
    return { ...state, loaders: { ...state.loaders, task: false } };
  }),

  on(ValidationActions.CreateTaskSuccess, (state, { discrepancies }): State => {
    const verifiedTasks = updateTasksOutcome(state.verifiedTasks, discrepancies);
    return { ...state, verifiedTasks, loaders: { ...state.loaders, task: false } };
  }),

  on(ValidationActions.UpdateTaskOutcome, (state): State => {
    return { ...state, loaders: { ...state.loaders, task: true } };
  }),

  on(ValidationActions.UpdateTaskOutcomeFailed, (state): State => {
    return { ...state, loaders: { ...state.loaders, task: false } };
  }),

  on(ValidationActions.UpdateTaskOutcomeSuccess, (state, { task }): State => {
    const verifiedTasks = updateTasksOutcome(state.verifiedTasks, [task]);
    return { ...state, verifiedTasks, loaders: { ...state.loaders, task: false } };
  }),

  on(ValidationActions.UpdateDocumentOutcome, (state): State => {
    return { ...state, loaders: { ...state.loaders, task: true } };
  }),

  on(ValidationActions.UpdateDocumentOutcomeFailed, (state): State => {
    return { ...state, loaders: { ...state.loaders, task: false } };
  }),

  on(ValidationActions.UpdateDocumentOutcomeSuccess, (state, { tasks }): State => {
    const verifiedTasks = updateTasksOutcome(state.verifiedTasks, tasks);
    return { ...state, verifiedTasks, loaders: { ...state.loaders, task: false } };
  }),

  on(ValidationActions.UpdateDocumentApplicability, (state): State => {
    return { ...state, loaders: { ...state.loaders, task: true } };
  }),

  on(ValidationActions.UpdateDocumentApplicabilityFailed, (state): State => {
    return { ...state, loaders: { ...state.loaders, task: false } };
  }),

  on(ValidationActions.UpdateDocumentApplicabilitySuccess, (state, { tasks }): State => {
    const verifiedTasks = tasks ? updateTasksOutcome(state.verifiedTasks, tasks) : state.verifiedTasks;
    return { ...state, verifiedTasks, loaders: { ...state.loaders, task: false } };
  }),

  on(ValidationActions.SetBulkDocumentApplicabilitySuccess, (state, { tasks }): State => {
    const verifiedTasks = tasks ? updateTasksOutcome(state.verifiedTasks, tasks) : state.verifiedTasks;
    return { ...state, verifiedTasks, loaders: { ...state.loaders, task: false } };
  }),

  on(ValidationActions.UpdateTask, (state): State => {
    return { ...state, loaders: { ...state.loaders, task: true } };
  }),

  on(ValidationActions.UpdateTaskFailed, (state): State => {
    return { ...state, loaders: { ...state.loaders, task: false } };
  }),

  on(ValidationActions.UpdateTaskSuccess, (state, { discrepancy }): State => {
    const verifiedTasks = updateTasksOutcome(state.verifiedTasks, [discrepancy]);
    return { ...state, verifiedTasks, loaders: { ...state.loaders, task: false } };
  })
);

export const reducer = (state: State, action: Action): State => validationReducer(state, action);

const updateVerifiedTasks = (verifiedTasks: VerifiedTasks, tlfName: string, tasks: IDiscrepancy[]) => {
  return { ...verifiedTasks, [tlfName]: tasks };
};

const updateTasksOutcome = (verifiedTasks: VerifiedTasks, discrepancies: IDiscrepancy[]) => {
  if (!discrepancies) return verifiedTasks;
  const updatedVerifiedTasks: VerifiedTasks = { ...verifiedTasks };
  const fileNameSet = new Set<string>();
  discrepancies.forEach((discrepancy) => {
    discrepancy.tlfs.forEach((tlf) => fileNameSet.add(tlf.file.name));
  });
  const fileNameToIdToTask = new Map();
  fileNameSet.forEach((name) => {
    const idToTask = new Map();
    updatedVerifiedTasks[name]?.forEach((task) => {
      idToTask.set(task.id, task);
    });
    fileNameToIdToTask.set(name, idToTask);
  });
  discrepancies.forEach((discrepancy) => {
    discrepancy.tlfs.forEach((tlf) => {
      const idToTask = fileNameToIdToTask.get(tlf.file.name);
      if (discrepancy.outcome === 'VERIFIED') {
        idToTask.set(discrepancy.id, discrepancy);
      } else {
        idToTask.delete(discrepancy.id);
      }
    });
  });
  fileNameSet.forEach((name) => {
    updatedVerifiedTasks[name] = Array.from(fileNameToIdToTask.get(name).values());
  });
  return updatedVerifiedTasks;
};
